import { getNodePositionInsideGroup } from "@features/builder/graph/getNodePositionInsideGroup"
import { sortNodes, updateSubNodesForGroup } from "@features/builder/graph/helpers"
import { getNodesIntersectingWithGroup } from "@features/builder/graph/intersection"
import { AppNode, EdgeData, GroupNode, isNotGroupNode } from "@features/builder/graph/types"
import { getRelativePosition } from "@features/builder/helpers/nodes"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { Edge, InternalNode, useReactFlow } from "@xyflow/react"

type ReactFlowReturnType = ReturnType<typeof useReactFlow<AppNode, Edge<EdgeData>>>

export const getNestedGroupsIds = (
  group: { id: GroupNode["id"] },
  allNodes: AppNode[],
): string[] => {
  const mapper = nodesGroupMapper(allNodes)

  return [group.id, ...(mapper.childToParentGroupsIdsMapDeep.get(group.id) ?? [])]
}

export function addNodesToTheGroup(
  groupNode: InternalNode<GroupNode>,
  nodesToMove: InternalNode<AppNode>[],
  allNodes: AppNode[],
): AppNode[] {
  let updatedNodes = structuredClone(allNodes)

  nodesToMove.forEach((internalNode) => {
    const nodeToMove = updatedNodes.find((node) => node.id === internalNode.id)
    if (!nodeToMove) return

    nodeToMove.position = getRelativePosition(
      internalNode.internals.positionAbsolute,
      groupNode.internals.positionAbsolute,
    )

    nodeToMove.position = getNodePositionInsideGroup(nodeToMove, groupNode)

    nodeToMove.parentId = groupNode.id
    nodeToMove.extent = "parent"

    if (!isNotGroupNode(nodeToMove)) {
      if (nodeToMove.data.metaGroup) {
        nodeToMove.data.metaGroup.parent = groupNode.id
      }
      updatedNodes = updateSubNodesForGroup(groupNode, nodeToMove, updatedNodes)
    } else {
      nodeToMove.data.metaNode.groups = getNestedGroupsIds(groupNode, allNodes)
    }
  })

  return updatedNodes
}

export function moveToGroup({
  allNodes,
  intersecting,
  getInternalNode,
}: {
  intersecting: ReturnType<typeof getNodesIntersectingWithGroup>
  allNodes: AppNode[]
  getInternalNode: ReactFlowReturnType["getInternalNode"]
}) {
  if (!intersecting.group) {
    return sortNodes(allNodes)
  }

  const groupInternal = getInternalNode(intersecting.group.id) as
    | InternalNode<GroupNode>
    | undefined

  const intersectingNodesInternals = intersecting.nodes
    .map((node) => getInternalNode(node.id))
    .filter((node) => node !== undefined)

  if (!groupInternal) {
    return sortNodes(allNodes)
  }

  let updatedNodes = structuredClone(allNodes)
  updatedNodes = addNodesToTheGroup(groupInternal, intersectingNodesInternals, updatedNodes)

  return sortNodes(updatedNodes)
}
